import { useState } from "react";
import SettingsLayout from "@/components/SettingsLayout";
import { SettingsList, SettingsListItem } from "@/components/SettingsList";
import { formatTimestamp } from "@/lib/dates";
import { csrfToken } from "@/lib/fetchSideEffect";

interface Device {
  id: string;
  name: string;
  paired_at: string;
  last_used_at: string | null;
}

export default function SettingsDevices({
  devices,
  android_app_version = null,
}: {
  devices: Device[];
  android_app_version?: string | null;
}) {
  const [revokingId, setRevokingId] = useState<string | null>(null);

  function startRevoke(id: string) {
    setRevokingId(id);
  }
  function cancelRevoke() {
    setRevokingId(null);
  }

  const deviceName =
    typeof navigator !== "undefined" ? (navigator.userAgent.split(";")[0]?.trim() || "Устройство") : "Устройство";

  // Pairing redirects to the revolver:// deep link, which only resolves on an Android
  // phone with the Capture app installed. Elsewhere the button would be a dead end, so we
  // show a hint pointing to the phone instead.
  const canPair =
    typeof navigator !== "undefined" && /Android/i.test(navigator.userAgent);

  return (
    <SettingsLayout active="devices" title="Устройства">
      {canPair ? (
        <div className="flex flex-wrap items-center justify-between gap-3">
          {android_app_version && (
            <a href="/settings/android_app" className="text-sm text-blue-600 hover:text-blue-700">
              Скачать приложение {android_app_version}
            </a>
          )}
          {/* Native form: follows 302 to revolver:// which browser hands to the OS */}
          <form method="POST" action="/settings/devices">
            <input type="hidden" name="authenticity_token" value={csrfToken()} />
            <input type="hidden" name="device[name]" value={deviceName} />
            <button
              type="submit"
              className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md font-medium"
            >
              Привязать устройство
            </button>
          </form>
        </div>
      ) : (
        <p className="text-sm text-gray-500">
          Привязка происходит на Android-телефоне: открой там Настройки → Устройства и нажми «Привязать устройство».
        </p>
      )}

      {devices.length === 0 ? (
        <p className="text-sm text-gray-500">Пока нет привязанных устройств.</p>
      ) : (
        <SettingsList>
          {devices.map((d) => (
            <SettingsListItem
              key={d.id}
              title={d.name}
              meta={
                <>
                  Привязано {formatTimestamp(d.paired_at)}
                  {d.last_used_at && <> · использовалось {formatTimestamp(d.last_used_at)}</>}
                </>
              }
              action={
                revokingId === d.id ? (
                  <form method="POST" action={`/settings/devices/${d.id}`} className="flex items-center gap-3">
                    <input type="hidden" name="authenticity_token" value={csrfToken()} />
                    <input type="hidden" name="_method" value="delete" />
                    <button
                      type="button"
                      onClick={cancelRevoke}
                      className="text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                    >
                      Отмена
                    </button>
                    <button
                      type="submit"
                      className="text-sm bg-red-600 hover:bg-red-700 text-white px-3 py-1.5 rounded-md"
                    >
                      Точно отозвать
                    </button>
                  </form>
                ) : (
                  <button
                    type="button"
                    onClick={() => startRevoke(d.id)}
                    className="text-sm text-red-600 hover:text-red-700"
                  >
                    Отозвать
                  </button>
                )
              }
            />
          ))}
        </SettingsList>
      )}
    </SettingsLayout>
  );
}
